import "@xyflow/react/dist/style.css";

import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type NodeMouseHandler,
} from "@xyflow/react";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { CardNode } from "./CardNode";
import { LabeledEdge } from "./LabeledEdge";
import { layoutGraph } from "./layout";
import type {
  CardNodeData,
  EntityGraphProps,
  LabeledEdgeData,
} from "./types";

export type {
  EntityGraphNode,
  EntityGraphEdge,
  EntityGraphFlow,
  EntityGraphProps,
} from "./types";

const nodeTypes = { entity: CardNode };
const edgeTypes = { labeled: LabeledEdge };

/** Scoped theme for the xyflow chrome (minimap / controls) via CSS vars. */
const THEME_CSS = `
.entity-graph-root .react-flow__minimap{background:hsl(var(--card));border:1px solid hsl(var(--border));border-radius:var(--radius)}
.entity-graph-root .react-flow__minimap-mask{fill:hsl(var(--muted)/0.4)}
.entity-graph-root .react-flow__controls{box-shadow:var(--shadow-sm);border-radius:var(--radius);overflow:hidden}
.entity-graph-root .react-flow__controls-button{background:hsl(var(--card));border-bottom:1px solid hsl(var(--border));color:hsl(var(--foreground))}
.entity-graph-root .react-flow__controls-button:hover{background:hsl(var(--accent))}
.entity-graph-root .react-flow__controls-button svg{fill:currentColor}
.entity-graph-root .react-flow__attribution{background:transparent;font-size:9px;opacity:0.5}
`;

function EntityGraphInner({
  nodes,
  edges,
  flows,
  layoutMode = "clusterGrid",
  direction = "LR",
  onNodeClick,
  height = "70vh",
  emptyMessage = "No graph data.",
  className,
}: EntityGraphProps) {
  const [activeFlow, setActiveFlow] = useState<string>("all");

  const { rfNodes, rfEdges } = useMemo(
    () => layoutGraph(nodes, edges, layoutMode, direction, flows, activeFlow),
    [nodes, edges, layoutMode, direction, flows, activeFlow],
  );

  const [flowNodes, setNodes, onNodesChange] =
    useNodesState<Node<CardNodeData>>(rfNodes);
  const [flowEdges, setEdges, onEdgesChange] =
    useEdgesState<Edge<LabeledEdgeData>>(rfEdges);

  // Re-seed when the laid-out arrays change (data, direction or active flow).
  useEffect(() => {
    setNodes(rfNodes);
  }, [rfNodes, setNodes]);
  useEffect(() => {
    setEdges(rfEdges);
  }, [rfEdges, setEdges]);

  const handleNodeClick: NodeMouseHandler = (_evt, node) => {
    const data = node.data as CardNodeData;
    onNodeClick?.(data.entity);
  };

  if (!nodes.length) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-xl border border-border bg-card text-sm text-muted-foreground",
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "entity-graph-root relative overflow-hidden rounded-xl border border-border bg-card",
        className,
      )}
      style={{ height }}
    >
      <style>{THEME_CSS}</style>

      {flows?.length ? (
        <div className="absolute left-2 top-2 z-10 flex flex-wrap gap-1.5 rounded-lg bg-background/85 p-1.5 backdrop-blur">
          <Button
            type="button"
            size="sm"
            variant={activeFlow === "all" ? "default" : "outline"}
            className="h-7 px-2.5 text-xs"
            onClick={() => setActiveFlow("all")}
          >
            All
          </Button>
          {flows.map((f) => (
            <Button
              key={f.id}
              type="button"
              size="sm"
              variant={activeFlow === f.id ? "default" : "outline"}
              className="h-7 gap-1.5 px-2.5 text-xs"
              onClick={() => setActiveFlow(f.id)}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: f.color }}
                aria-hidden
              />
              {f.label}
            </Button>
          ))}
        </div>
      ) : null}

      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.14, maxZoom: 1.1 }}
        minZoom={0.1}
        maxZoom={3}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        panOnDrag
        zoomOnScroll
        proOptions={{ hideAttribution: false }}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1} />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable />
      </ReactFlow>
    </div>
  );
}

/**
 * Reusable data-driven entity graph (React Flow port of
 * `docs/architecture-map.html`): white left-accent cards with glyph + title +
 * subtitle + 2-line description + tag; curved colored labeled edges (solid or
 * dashed); pan / zoom / drag / minimap; an optional flow/legend filter bar.
 */
export function EntityGraph(props: EntityGraphProps) {
  return (
    <ReactFlowProvider>
      <EntityGraphInner {...props} />
    </ReactFlowProvider>
  );
}
